/**
 * Fliken Kundprojekt i Affärer – Pipeline: lista med filter och den förvalda
 * snabbvyn "Kommande granskningsfas". Klick på en rad öppnar kundprojektets
 * sida (/crm/kundprojekt/:id).
 *
 * Kundprojekt är kundens projekt som säljunderlag – inte Tikabs uppdrag.
 */
import React, { useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Chip, TextField, MenuItem, FormControlLabel, Switch, Button, CircularProgress, Tooltip,
} from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import {
  etikett, filtreraKundprojekt, sorteraPaSkedestart, arKommandeGranskningsfas, manadText, TOMT_KP_FILTER,
  KP_BYGGSKEDEN, KP_ENTREPRENADFORMER, KP_ROLLER, KP_STORLEKSKLASSER, KP_STATUSAR, JA_NEJ_OKANT, SKEDESTART_INOM,
  GRANSKNING_INOM_MANADER,
} from './kundprojekt';
import { SakerhetsIkoner } from './KundprojektVyer';

function Val({ label, value, onChange, lista, minWidth = 150 }) {
  return (
    <TextField select size="small" label={label} value={value} onChange={e => onChange(e.target.value)}
      sx={{ minWidth, bgcolor: 'white' }}>
      <MenuItem value="">Alla</MenuItem>
      {lista.map(x => <MenuItem key={x.key} value={x.key}>{x.label}</MenuItem>)}
    </TextField>
  );
}

/**
 * @param {object} p
 *   kundprojekt  alla kundprojekt
 *   companies    kunder (för parternas namn)
 *   navigate
 *   laddar
 */
export default function KundprojektListvy({ kundprojekt, companies, navigate, laddar }) {
  const [snabbvy, setSnabbvy] = useState(true);
  const [filter, setFilter] = useState(TOMT_KP_FILTER);
  const satt = (k, val) => setFilter(f => ({ ...f, [k]: val }));
  const kundNamn = useMemo(() => new Map(companies.map(c => [c.id, c.name])), [companies]);

  const nu = Date.now();
  const urval = filtreraKundprojekt(snabbvy ? kundprojekt.filter(kp => arKommandeGranskningsfas(kp, nu)) : kundprojekt, filter, nu);
  const rader = snabbvy
    ? sorteraPaSkedestart(urval)
    : [...urval].sort((a, b) => String(a.namn).localeCompare(String(b.namn), 'sv'));
  const filterAktiva = Object.entries(filter).some(([, x]) => x);

  const th = { px: 1.5, py: 1, textAlign: 'left', fontWeight: 700, fontSize: '0.72rem', color: 'text.secondary',
    textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid', borderColor: 'divider', whiteSpace: 'nowrap' };
  const td = { px: 1.5, py: 1, fontSize: '0.82rem', verticalAlign: 'top' };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1.25, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Tooltip title={`Programhandling eller systemhandling, eller skedestart inom ${GRANSKNING_INOM_MANADER} månader. Bara aktiva. Sorterad på skedestart.`}>
          <Chip icon={<ScheduleIcon />} label="Kommande granskningsfas" clickable
            onClick={() => setSnabbvy(v => !v)} variant={snabbvy ? 'filled' : 'outlined'}
            sx={snabbvy
              ? { bgcolor: '#FF6B00', color: 'white', fontWeight: 700, '& .MuiChip-icon': { color: 'white' }, '&:hover': { bgcolor: '#e55a00' } }
              : { fontWeight: 600, bgcolor: 'white' }} />
        </Tooltip>
        <Val label="Byggskede" lista={KP_BYGGSKEDEN} value={filter.byggskede} onChange={x => satt('byggskede', x)} />
        <Val label="Skedestart" lista={SKEDESTART_INOM} value={filter.skedestartInom} onChange={x => satt('skedestartInom', x)} minWidth={160} />
        <Val label="Entreprenadform" lista={KP_ENTREPRENADFORMER} value={filter.entreprenadform} onChange={x => satt('entreprenadform', x)} minWidth={170} />
        <Val label="Roll" lista={KP_ROLLER} value={filter.roll} onChange={x => satt('roll', x)} />
        <Val label="Storleksklass" lista={KP_STORLEKSKLASSER} value={filter.storleksklass} onChange={x => satt('storleksklass', x)} />
        <Val label="Säkerhetsskydd" lista={JA_NEJ_OKANT} value={filter.sakerhetsskydd} onChange={x => satt('sakerhetsskydd', x)} />
        <Val label="Status" lista={KP_STATUSAR} value={filter.status} onChange={x => satt('status', x)} minWidth={120} />
        <FormControlLabel sx={{ ml: 0.5 }}
          control={<Switch size="small" checked={filter.isolerad} onChange={e => satt('isolerad', e.target.checked)}
            sx={{ '& .Mui-checked': { color: '#6A1B9A' }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: '#6A1B9A' } }} />}
          label={<Typography variant="body2">Isolerad/airgappad miljö</Typography>} />
        {filterAktiva && (
          <Button size="small" onClick={() => setFilter(TOMT_KP_FILTER)} sx={{ color: '#FF6B00', textTransform: 'none' }}>
            Rensa filter
          </Button>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {rader.length} av {kundprojekt.length} kundprojekt
        </Typography>
      </Box>

      <Paper sx={{ overflow: 'hidden' }}>
        {laddar ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : rader.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
            {kundprojekt.length === 0
              ? 'Inga kundprojekt registrerade än. Registrera dem med "+ Kundprojekt" på kundkortet, en kontakt eller ett lead.'
              : snabbvy ? 'Inga kundprojekt i kommande granskningsfas med de här filtren.' : 'Inga kundprojekt matchar filtren.'}
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <Box component="thead">
                <Box component="tr" sx={{ bgcolor: '#f5f5f5' }}>
                  {['Kundprojekt', 'Parter', 'Byggskede', 'Skedestart', 'Entreprenadform', 'Storlek', 'Leads', 'Status'].map(h => (
                    <Box key={h} component="th" sx={th}>{h}</Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {rader.map(kp => {
                  const antalLeads = new Set((kp.parter || []).flatMap(p => p.leadIds || [])).size;
                  return (
                    <Box component="tr" key={kp.id} onClick={() => navigate(`/crm/kundprojekt/${kp.id}`)}
                      tabIndex={0} onKeyDown={e => { if (e.key === 'Enter') navigate(`/crm/kundprojekt/${kp.id}`); }}
                      sx={{ cursor: 'pointer', borderBottom: '1px solid', borderColor: 'divider',
                        '&:hover': { bgcolor: '#fffaf6' }, '&:focus-visible': { outline: '2px solid #FF6B00' } }}>
                      <Box component="td" sx={td}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="body2" fontWeight={700}>{kp.namn}</Typography>
                          <SakerhetsIkoner kp={kp} />
                        </Box>
                        {kp.ort && <Typography variant="caption" color="text.secondary">{kp.ort}</Typography>}
                      </Box>
                      <Box component="td" sx={td}>
                        {(kp.parter || []).map((p, i) => (
                          <Typography key={`${p.kundId}-${i}`} variant="caption" sx={{ display: 'block' }}>
                            {kundNamn.get(p.kundId) || 'Okänd kund'}
                            {p.roll && <Box component="span" sx={{ color: 'text.secondary' }}> · {etikett(KP_ROLLER, p.roll)}</Box>}
                          </Typography>
                        ))}
                      </Box>
                      <Box component="td" sx={td}>{etikett(KP_BYGGSKEDEN, kp.byggskede) || '–'}</Box>
                      <Box component="td" sx={{ ...td, whiteSpace: 'nowrap' }}>{manadText(kp.skedeStart) || '–'}</Box>
                      <Box component="td" sx={td}>{etikett(KP_ENTREPRENADFORMER, kp.entreprenadform) || '–'}</Box>
                      <Box component="td" sx={{ ...td, whiteSpace: 'nowrap' }}>{etikett(KP_STORLEKSKLASSER, kp.storleksklass) || '–'}</Box>
                      <Box component="td" sx={td}>{antalLeads || '–'}</Box>
                      <Box component="td" sx={td}>{etikett(KP_STATUSAR, kp.status || 'aktivt')}</Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
